import { AppClaims } from '@app/cp/shared/models/AppClaims';

export interface NavItem {
    label: string;
    icon: string;
    route?: string;
    /** Any permission listed = show the item (OR logic) */
    permissions?: (keyof AppClaims)[];
    /** Feature flag that must be TRUTHY to show this item */
    featureFlag?: string;
    /** Feature flag that must be FALSY to show this item */
    featureFlagHide?: string;
    /** Deployments where this item is visible; omit = all */
    envs?: string[];
    /** Hide for Zuggit users */
    zuggitHide?: boolean;
    /** Show ONLY for Zuggit users */
    zuggitOnly?: boolean;
    /** Simple action key — no arguments */
    action?: 'downloads' | 'uploads' | 'expenses-maint' | 'expenses-submit' | 'time-off-calendar';
    /** Dialog/popup action key — dispatched to ViewChild methods in component */
    dialogAction?: 'credit-memo-profiles' | 'warehouses' | 'shipping-methods' |
        'billing-codes' | 'manufacturers' | 'manufacturer-type' |
        'part-type' | 'part-category' | 'part-subcategory' | 'table-maint-test';
    /** Whether this item opens in a new tab (external links) */
    external?: boolean;
    /** href for external links (used instead of route) */
    href?: string;
    children?: NavItem[];
}

export const NAV_ITEMS: NavItem[] = [

    // ── Dashboard ─────────────────────────────────────────────────────────────
    {
        label: 'Dashboard',
        icon: 'fa-tachometer-alt',
        route: '/dashboard',
    },

    // ── Boards ────────────────────────────────────────────────────────────────
    {
        label: 'Boards',
        icon: 'fa-chalkboard',
        permissions: [ 'ViewDispatchBoard', 'ViewDispatchBoardFG2', 'ViewConstructionPOInformation' ],
        children: [
            { label: 'Dispatch Board', icon: 'fa-th', route: '/boards/dispatchboardcalendar', permissions: [ 'ViewDispatchBoard', 'ViewDispatchBoardFG2' ], featureFlagHide: 'ui-hide-zuggit-menu-items-extended' },
            { label: 'Open PO Info', icon: 'fa-box-open', route: '/orderinginventory/openpoinfo', permissions: [ 'ViewConstructionPOInformation' ], zuggitHide: true },
            { label: 'Zuggit Open PO Info', icon: 'fa-box-open', route: '/orderinginventory/openpoinfo', permissions: [ 'ViewConstructionPOInformation' ], zuggitOnly: true },
            { label: 'PO Bills', icon: 'fa-box-open', route: '/orderinginventory/openpobillsinfo', permissions: [ 'POBillsCredits' ] },
            { label: 'PO Vendor Credits', icon: 'fa-box-open', route: '/orderinginventory/openpovendorcreditsinfo', permissions: [ 'POBillsCredits' ] },
            { label: 'Sub Contractor POs', icon: 'fa-box-open', route: '/orderinginventory/openposubinfo', permissions: [ 'ViewConstructionSubPOInformation' ], featureFlagHide: 'ui-hide-zuggit-menu-items-extended' },
        ],
    },

    // ── Service ───────────────────────────────────────────────────────────────
    {
        label: 'Service',
        icon: 'fa-phone',
        children: [
            { label: 'Dispatches', icon: 'fa-phone', route: '/dispatch/search', featureFlagHide: 'ui-hide-zuggit-menu-items-extended' },
            { label: 'Sales Order', icon: 'fa-phone', route: '/dispatch/search', featureFlag: 'ui-hide-zuggit-menu-items-extended' },
            { label: 'Quotes', icon: 'fa-quote-right', route: '/quote/search', permissions: [ 'ViewQuotes' ] },
            { label: 'Orders', icon: 'fa-receipt', route: '/order/search', permissions: [ 'ViewOrders' ] },
            { label: 'Invoices', icon: 'fa-dollar-sign', route: '/invoice/search', permissions: [ 'ViewInvoices' ] },
            { label: 'Dispatch History', icon: 'fa-history', route: '/dispatch/history', permissions: [ 'ViewDispatchHistory' ] },
            { label: 'Invoice History', icon: 'fa-history', route: '/dispatch/invoiceAction' },
        ],
    },

    // ── Construction ──────────────────────────────────────────────────────────
    {
        label: 'Construction',
        icon: 'fa-toolbox',
        permissions: [ 'ViewConstruction' ],
        featureFlagHide: 'ui-hide-zuggit-menu-items-extended',
        children: [
            { label: 'Contracts', icon: 'fa-file-contract', route: '/construction/contracts' },
            { label: 'Jobs', icon: 'fa-hard-hat', route: '/construction/jobs' },
            { label: 'Quotes', icon: 'fa-quote-right', route: '/construction/quote', permissions: [ 'ViewQuotes' ] },
            { label: 'Orders', icon: 'fa-receipt', route: '/construction/order', permissions: [ 'ViewOrders' ] },
            { label: 'Invoices', icon: 'fa-dollar-sign', route: '/construction/invoice', permissions: [ 'ViewInvoices' ] },
            { label: 'Job Information', icon: 'fa-info-circle', route: '/construction/job-information', permissions: [ 'ViewJobInformation' ] },
            { label: 'Open Contract Info', icon: 'fa-envelope-open-text', route: '/construction/opencontract', permissions: [ 'ViewContractInfo' ] },
            { label: 'PM Board', icon: 'fa-envelope-open-text', route: '/construction/pmboard', permissions: [ 'ViewConstPMBoard' ] },
            { label: 'WIP Contract Report', icon: 'fa-briefcase', route: '/construction/ContractWipboard', permissions: [ 'WIPContractReport' ] },
            { label: 'Equipment Job Board', icon: 'fa-briefcase', route: '/construction/EquipmentBoard', permissions: [ 'WIPContractReport' ] },
            { label: 'Contract History', icon: 'fa-history', route: '/construction/contracthistory', permissions: [ 'ViewContractHistory' ] },
        ],
    },

    // ── Customers ─────────────────────────────────────────────────────────────
    {
        label: 'Customers',
        icon: 'fa-building',
        route: '/customers',
        permissions: [ 'ViewCustomers' ],
    },

    // ── Inventory ─────────────────────────────────────────────────────────────
    {
        label: 'Inventory',
        icon: 'fa-inventory',
        children: [
            { label: 'Parts', icon: 'fa-box', route: '/orderinginventory/Parts', permissions: [ 'ViewParts' ] },
            { label: 'Warehouse Transfers', icon: 'fa-warehouse', route: '/orderinginventory/warehousetransfers', permissions: [ 'WarehouseTransfers' ] },
            { label: 'Asset Tracking', icon: 'fa-chart-line', route: '/orderinginventory/assettracking', permissions: [ 'MaintainAssets' ], featureFlagHide: 'ui-hide-zuggit-menu-items-extended' },
            { label: 'Stock Replenishment', icon: 'fa-chart-line', route: '/orderinginventory/StockReplenishment', permissions: [ 'StockReplenishment' ] },
            { label: 'Parts History', icon: 'fa-history', route: '/orderinginventory/partshistory', permissions: [ 'ViewParts' ] },
            { label: 'Parts Merge History', icon: 'fa-history', route: '/orderinginventory/partsmerge', permissions: [ 'ViewParts' ] },
            { label: 'Parts Cost History', icon: 'fa-history', route: '/orderinginventory/partscosthistory', permissions: [ 'ViewParts' ] },
            { label: 'Cycle Counting', icon: 'fa-calculator', route: '/orderinginventory/cycleCounting', permissions: [ 'canViewCycleCounting', 'canCreateInventoryCount', 'inventoryCountAdmin', 'canEditInventoryCount', 'canCommitInventoryCount' ] },
            { label: 'Cycle Counting History', icon: 'fa-history', route: '/orderinginventory/cyclecountinghistory', permissions: [ 'canViewCycleCounting', 'canCreateInventoryCount', 'inventoryCountAdmin', 'canEditInventoryCount', 'canCommitInventoryCount' ], featureFlag: 'ui-cycle-counting-history' },
        ],
    },

    // ── Suppliers ─────────────────────────────────────────────────────────────
    {
        label: 'Suppliers',
        icon: 'fa-store-alt',
        children: [
            { label: 'Suppliers', icon: 'fa-store-alt', route: '/orderinginventory/vendors', permissions: [ 'MaintainSuppliers', 'ViewSupplier' ] },
            { label: 'Supplier Returns', icon: 'fa-undo', route: '/orderinginventory/vendorreturns' },
            { label: 'Supplier Credits', icon: 'fa-hand-holding-usd', route: '/orderinginventory/vendorcredits' },
            { label: 'Order Search', icon: 'fa-shopping-cart', route: '/orderinginventory/ordersearch' },
        ],
    },

    // ── Downloads / Uploads ───────────────────────────────────────────────────
    {
        label: 'Downloads',
        icon: 'fa-file-download',
        action: 'downloads',
        featureFlagHide: 'ui-hide-zuggit-menu-items-extended',
    },
    {
        label: 'Uploads',
        icon: 'fa-file-upload',
        action: 'uploads',
        permissions: [ 'CollectionBulkUpload', 'BulkUploadInventory' ],
    },

    // ── Reports ───────────────────────────────────────────────────────────────
    // children: [] intentional — populated at runtime via reportAccess pipe in template
    {
        label: 'Reports',
        icon: 'fa-chart-line',
        children: [],
    },

    // ── Human Resources ───────────────────────────────────────────────────────
    {
        label: 'Human Resources',
        icon: 'fa-star-of-life',
        children: [
            { label: 'Employee Maint', icon: 'fa-users', route: '/humanresources/employeemaintenance', permissions: [ 'ViewEmployeeMaintenance' ] },
            { label: 'Payroll Maintenance', icon: 'fa-file-invoice-dollar', route: '/payroll/payrollmaintenance', permissions: [ 'ViewPayrollandTimesheets' ] },
            { label: 'Benefit Mapping', icon: 'fa-map-signs', route: '/payroll/benefitmapping', permissions: [ 'MaintainBenefitMapping' ] },
            { label: 'Time Exclusions', icon: 'fa-map-signs', route: '/payroll/timeexclusions', permissions: [ 'MaintainBenefitMapping' ] },
            { label: 'Time Off Approval', icon: 'fa-plane-departure', route: '/payroll/timeoffapproval', permissions: [ 'MaintainTimeOffRequests' ] },
            { label: 'Expense Maint', icon: 'fa-hand-holding-usd', route: '/payroll/expensemaintenancepayroll', permissions: [ 'AdminExpenseMaintenance' ] },
        ],
    },

    // ── Expenses ──────────────────────────────────────────────────────────────
    {
        label: 'Expenses',
        icon: 'fa-receipt',
        children: [
            { label: 'Expense Approval', icon: 'fa-receipt', action: 'expenses-maint' },
            { label: 'Expense Submission', icon: 'fa-file-invoice-dollar', action: 'expenses-submit' },
        ],
    },

    // ── Fleet Management ──────────────────────────────────────────────────────
    {
        label: 'Fleet Management',
        icon: 'fa-truck',
        children: [
            { label: 'Vehicles', icon: 'fa-truck', route: '/fleetmanagement/vehicles', permissions: [ 'MaintainVehicles' ] },
        ],
    },

    // ── Accounting ────────────────────────────────────────────────────────────
    {
        label: 'Accounting',
        icon: 'fa-book',
        permissions: [ 'PriceChangeBenefit' ],
        children: [
            { label: 'Price Change Benefit', icon: 'fa-chart-area', route: '/accounting/PriceChangeBenefit', permissions: [ 'PriceChangeBenefit' ] },
        ],
    },

    // ── Time Management ───────────────────────────────────────────────────────
    {
        label: 'Time Management',
        icon: 'fa-clock',
        children: [
            { label: 'Time Approval', icon: 'fa-file-invoice-dollar', route: '/payroll/timeapproval' },
            { label: 'Time Off Calendar', icon: 'fa-calendar-alt', action: 'time-off-calendar' },
            { label: 'Clock In/Out', icon: 'fa-alarm-clock', route: '/mylinks/clockinout', permissions: [ 'ClockinClockOutTime' ] },
            { label: 'Request Time Off', icon: 'fa-plane-departure', route: '/mylinks/requesttimeoff', permissions: [ 'RequestTimeOff' ] },
        ],
    },

    // ── Ref Tracking  ──────────────────────────────────────────────────────────
   /* {
        label: 'Ref Tracking Admin',
        icon: 'fa-thermometer-quarter',
        permissions: [ ], // TODO: NEEDS PERMISSION BEFORE WE CAN SHOW IN PROD
        route: '/reftracking/admin',
    },*/

    // ── System Configuration ──────────────────────────────────────────────────
    {
        label: 'System Configuration',
        icon: 'fa-cog',
        featureFlagHide: 'ui-hide-zuggit-menu-items-extended',
        children: [
            { label: 'Event Manager', icon: 'fa-calendar-check', route: '/systemConfig/event-manager', permissions: [ 'MaintainSystemEvents' ] },
            { label: 'Zebra Print Config', icon: 'fa-calendar-check', route: '/systemConfig/zebra-print-config', permissions: [ 'MaintainZebraPrinters' ], featureFlag: 'distributed-printing-for-zebra' },
            { label: 'Table Maintenance', icon: 'fa-table', children: [
                    { label: 'Credit Memo User Profiles', icon: 'fa-receipt', dialogAction: 'credit-memo-profiles', permissions: [ 'MaintainUserCreditMemoProfiles' ], featureFlag: 'credit-memo-approval-widget-high-priority-changes-pt-2' },
                    { label: 'Warehouses', icon: 'fa-warehouse', dialogAction: 'warehouses' },
                    { label: 'Shipping Methods', icon: 'fa-shipping-fast', dialogAction: 'shipping-methods' },
                    { label: 'Billing Codes', icon: 'fa-file-invoice-dollar', dialogAction: 'billing-codes' },
                    { label: 'Manufacturers', icon: 'fa-industry', dialogAction: 'manufacturers' },
                    { label: 'Manufacturer Type', icon: 'fa-layer-group', dialogAction: 'manufacturer-type' },
                    { label: 'Inventory Part Type', icon: 'fa-boxes', dialogAction: 'part-type', permissions: [ 'EditCategoriesandType' ], featureFlag: 'add-edit-subcategory-in-inventory-parts' },
                    { label: 'Inventory Category', icon: 'fa-folder', dialogAction: 'part-category', permissions: [ 'EditCategoriesandType' ], featureFlag: 'add-edit-subcategory-in-inventory-parts' },
                    { label: 'Inventory Sub Category', icon: 'fa-folder-open', dialogAction: 'part-subcategory', permissions: [ 'EditCategoriesandType' ], featureFlag: 'add-edit-subcategory-in-inventory-parts' }
                ],
            },
            { label: 'Mobile Maintenance', icon: 'fa-mobile-alt', route: '/systemConfig/mobile', permissions: [ 'MaintainMobileCustomForms' ] },
            { label: 'User Settings', icon: 'fa-screwdriver', route: '/mylinks/usersettings' },
        ],
    },

    // ── Quick Links ───────────────────────────────────────────────────────────
    {
        label: 'Quick Links',
        icon: 'fa-link',
        children: [
            { label: 'Employee Intranet', icon: 'fa-external-link-alt', href: 'https://climateprosinc.sharepoint.com/sites/CPIntranet', external: true },
            { label: 'CPU Technical Training', icon: 'fa-external-link-alt', href: 'https://climateprosinc.sharepoint.com/sites/ClimateProsUniversity', external: true },
            { label: 'Event Calendar', icon: 'fa-external-link-alt', href: 'https://climateprosinc.sharepoint.com/sites/ClimateProsEventCalendar/Lists/Climate%20Pros%20Calendar/calendar.aspx', external: true },
            { label: 'Acadia', icon: 'fa-external-link-alt', href: 'https://climatepros.acadia.sysalli.com/browse', external: true },
            { label: 'Brainier', icon: 'fa-external-link-alt', href: 'https://climatepros.brainier.com/', external: true },
            { label: 'ADP', icon: 'fa-external-link-alt', href: 'https://workforcenow.adp.com', external: true },
            { label: 'IT Help', icon: 'fa-envelope', href: 'mailto:help@climatepros.com', external: true },
            { label: 'Freshservice Portal', icon: 'fa-bolt', href: 'https://climatepros.freshservice.com/support/home', external: true },
            { label: 'Freshservice Catalog', icon: 'fa-bolt', href: 'https://climatepros.freshservice.com/support/catalog/items', external: true },
        ],
    },
];
